import type { TaskFilterShared } from '@staiter/shared/task-module';
import { create } from 'zustand';

/**
 * chat-interactive-actions F1 (SOLUTION §3.1, I5) — фильтр задач как ОБЩЕЕ состояние.
 *
 * До пака фильтр жил в `useState` двух экранов (`_auth.work-tasks.index.tsx` и
 * `ProjectLayoutRoot.tsx`). Снаружи задать его было нечем: агент мог только
 * рассказать, какой фильтр стоило бы применить. Инвариант I5 — «фильтр от агента и
 * фильтр от руки это одна модель» — из локального `useState` недостижим по
 * устройству: у React-состояния нет входа, кроме самого компонента.
 *
 * Ключ поверхности — `global` или `project:<uuid>`: у проекта свой фильтр, и
 * писать их в одну ячейку значило бы, что переход в проект молча меняет глобальный
 * список (и наоборот).
 *
 * `version` — счётчик записей. По нему карточка фильтра понимает, что состояние
 * пришло ИЗВНЕ, и пересобирает свой draft [П1]: без счётчика её отложенный на
 * 350 мс коммит затирал бы только что применённый агентом фильтр — беззвучно и
 * через полсекунды после того, как человек увидел результат.
 */

/** Ключ поверхности: глобальный список или конкретный проект. */
export type TaskFilterSurfaceKey = 'global' | `project:${string}`;

export const projectSurfaceKey = (projectId: string): TaskFilterSurfaceKey =>
  `project:${projectId}`;

type SurfaceState = {
  filter: TaskFilterShared;
  version: number;
  /**
   * Эфемерный (не persist) override тумблера «показывать закрытые» — только для
   * записи агента [П10]. Пользовательское предпочтение живёт в
   * `workTasksViewStore` и сохраняется между сессиями; агентский показ закрытых
   * относится к ОДНОМУ ответу и переживать перезагрузку не должен.
   * `null` — override не задан, читается пользовательский pref.
   */
  showClosedOverride: boolean | null;
  /** Значение pref на момент установки override — «Сбросить» возвращает его. */
  showClosedBefore: boolean | null;
  /** Записан ли текущий фильтр агентом: от этого зависит чип «Фильтр из чата». */
  fromChat: boolean;
  /**
   * F18 — id колонки, которую изменит предложение чата. Экран подводит её в
   * кадр после успешного запроса. `null` — подводить нечего (метки,
   * исполнители, комментарий: там меняется не одно скалярное поле).
   */
  revealColumn: string | null;
};

type State = {
  surfaces: Record<string, SurfaceState>;
};

type Actions = {
  getSurface: (key: TaskFilterSurfaceKey) => SurfaceState;
  /** Ручная запись (карточка фильтра, saved view, строка поиска). */
  setFilter: (key: TaskFilterSurfaceKey, filter: TaskFilterShared) => void;
  /**
   * Запись агента: тот же фильтр, плюс отметка происхождения и эфемерный
   * override показа закрытых. `currentShowClosed` — pref на момент применения,
   * чтобы «Сбросить» вернул именно его, а не дефолт.
   */
  applyFromChat: (
    key: TaskFilterSurfaceKey,
    filter: TaskFilterShared,
    opts?: {
      showClosed?: boolean | undefined;
      currentShowClosed?: boolean;
      /** F18 — колонка изменяемого поля; экран подведёт её в кадр. */
      revealColumn?: string | null;
    },
  ) => void;
  /** Сброс поверхности: фильтр пуст, override снят, происхождение забыто. */
  resetFilter: (key: TaskFilterSurfaceKey) => void;
  /** Смена компании/охвата: чужие данные не должны просвечивать сквозь фильтр. */
  resetAll: () => void;
  /** Уход с проекта: ключ `project:<id>` не должен копиться до конца сессии. */
  dropSurface: (key: TaskFilterSurfaceKey) => void;
};

export const EMPTY_SURFACE: SurfaceState = {
  filter: {},
  version: 0,
  revealColumn: null,
  showClosedOverride: null,
  showClosedBefore: null,
  fromChat: false,
};

export const useTaskFilterStore = create<State & Actions>((set, get) => ({
  surfaces: {},

  getSurface(key) {
    return get().surfaces[key] ?? EMPTY_SURFACE;
  },

  setFilter(key, filter) {
    const prev = get().surfaces[key] ?? EMPTY_SURFACE;
    set({
      surfaces: {
        ...get().surfaces,
        [key]: {
          ...prev,
          filter,
          version: prev.version + 1,
          // Ручная правка снимает и отметку происхождения, и override: чип
          // «применено из чата» после ручного вмешательства был бы неправдой.
          fromChat: false,
          revealColumn: null,
          showClosedOverride: null,
          showClosedBefore: null,
        },
      },
    });
  },

  applyFromChat(key, filter, opts) {
    const prev = get().surfaces[key] ?? EMPTY_SURFACE;
    const override = opts?.showClosed;
    set({
      surfaces: {
        ...get().surfaces,
        [key]: {
          filter,
          version: prev.version + 1,
          fromChat: true,
          revealColumn: opts?.revealColumn ?? null,
          showClosedOverride: override === undefined ? null : override,
          showClosedBefore:
            override === undefined ? null : (opts?.currentShowClosed ?? prev.showClosedBefore),
        },
      },
    });
  },

  resetFilter(key) {
    const prev = get().surfaces[key] ?? EMPTY_SURFACE;
    set({
      surfaces: {
        ...get().surfaces,
        [key]: { ...EMPTY_SURFACE, version: prev.version + 1 },
      },
    });
  },

  resetAll() {
    // version не сохраняем: смена компании — это другой набор данных целиком,
    // и подписчикам правильнее увидеть свежую поверхность, а не «то же, но пустое».
    set({ surfaces: {} });
  },

  dropSurface(key) {
    const next = { ...get().surfaces };
    delete next[key];
    set({ surfaces: next });
  },
}));

/** Подписка поверхности: фильтр + версия + признаки записи агента. */
export function useTaskFilterSurface(key: TaskFilterSurfaceKey): SurfaceState {
  return useTaskFilterStore((s) => s.surfaces[key] ?? EMPTY_SURFACE);
}
